import { ANY_ODDS, bestLines, bestMarket, meanFromPmf, sideEv, withinOddsRange, type BestLineEligibility, type MarketPmfs, type Pick, type PropLine } from '@sharpr/pricing';
import { viewerScenario, type PlayerProjection, type Side } from '@sharpr/projections';
import type { BestLine, BookLine, MarketCode, PlayerWithMarkets } from '@sharpr/types';
import { groupBy, isShallowEqual } from 'remeda';
import { getPlayerImageUrl } from '@/utils/playerImages';
import { boardPlayerKey, playerOf, type Board, type BoardGame, type BoardPlayer } from './apply';

type Markets = PlayerWithMarkets['markets'];

// The board priced in the browser (spec §4.4) into the rows the odds screen renders. Books are ids, as the user's
// book preferences are keyed; only a side `eligibility` allows wins a best line (the viewer's excluded books and liquidity floor). `out` is the
// chips the viewer has marked out (boardPlayerKey, per game: a team can be on the board twice); a projected game prices
// at the scenario its ruled-out players and those marks make (spec §3.2), and every projected field of a row (PMFs,
// minutes) comes from that scenario. A pricer keeps each player's row until its lines, player, game, scenario or the
// books change, so a flush that moved one line re-prices one row and memoized rows don't re-render.
export function rowPricer() {
  let cache = new Map<string, { player: BoardPlayer; game: BoardGame | undefined; projection: PlayerProjection | undefined; scenario: string; lines: PropLine[]; eligibility: BestLineEligibility; seed: Board['seed']; row: PlayerWithMarkets }>();
  return (board: Board, eligibility: BestLineEligibility, out: ReadonlySet<string> = NO_OUT): PlayerWithMarkets[] => {
    // A line priced outside the seed's odds range is no line: no cell, no best line, and a player left with none is no row.
    const range = board.seed.oddsRange ?? ANY_ODDS;
    const byPlayer = groupBy([...board.lines.values()].filter(l => withinOddsRange(range, l)), playerOf);
    const choices = scenarioChoice(board, out);
    const next: typeof cache = new Map();
    const rows: PlayerWithMarkets[] = [];
    for (const [k, lines] of Object.entries(byPlayer)) {
      const p = board.players.get(k);
      const choice = p && choices.get(p.gameId);
      if (!p || choice?.out.has(p.playerId)) continue;
      const game = board.games.get(p.gameId);
      const projection = choice?.offered ? choice.scenario[p.playerId] : undefined;
      // A row says its scenario where the viewer can choose one (chips), or where the one it prices at isn't modeled.
      const scenario = choice && (choice.chips.size > 0 || !choice.offered)
        ? { out: [...choice.out].map(id => choice.chips.get(id)!).sort(), outPlayerIds: [...choice.out].sort(), modeled: choice.offered } : undefined;
      const sig = scenario ? JSON.stringify(scenario) : '';
      const c = cache.get(k);
      const same = c && c.player === p && c.game === game && c.projection === projection && c.scenario === sig && c.eligibility === eligibility
        && c.seed === board.seed && isShallowEqual(c.lines, lines);
      let priced = c?.row;
      if (!same) {
        const r = row(p, game, board.seed, projection, lines, eligibility);
        priced = scenario ? { ...r, scenario, ...(choice!.chips.has(p.playerId) && { questionable: true }) } : r;
      }
      next.set(k, { player: p, game, projection, scenario: sig, lines, eligibility, seed: board.seed, row: priced! });
      rows.push(priced!);
    }
    cache = next;
    return rows;
  };
}

const NO_OUT: ReadonlySet<string> = new Set();
// An odds-only player's PMFs.
const NO_PMFS: MarketPmfs = {};
const OTHER: Record<Side, Side> = { home: 'away', away: 'home' };

// Per projected game: its chips (id → name), the ones the viewer has out, and the scenario its ruled-out players and
// those marks make (§3.2). A scenario the game does not offer → the whole game odds-only.
interface GameChoice { chips: Map<string, string>; out: Set<string>; offered: boolean; scenario: Record<string, PlayerProjection> }

function scenarioChoice(board: Board, out: ReadonlySet<string>): Map<string, GameChoice> {
  const choices = new Map<string, GameChoice>();
  for (const { gameId, projection } of board.games.values()) {
    if (!projection) continue;
    const chips = new Map(projection.questionable.map(q => [q.playerId, q.name]));
    const { key, marked } = viewerScenario(projection.ruledOut, [...chips.keys()], id => out.has(boardPlayerKey(gameId, id)));
    const scenario = projection.scenarios[key];
    choices.set(gameId, { chips, out: new Set(marked), offered: scenario !== undefined, scenario: scenario ?? {} });
  }
  return choices;
}

function row(p: BoardPlayer, game: BoardGame | undefined, seed: Board['seed'], projection: PlayerProjection | undefined, lines: PropLine[],
  eligibility: BestLineEligibility): PlayerWithMarkets {
  const pmfs = projection?.marketPmfs ?? NO_PMFS;
  const team = p.side && game?.teams[p.side];
  const opponent = p.side && game?.teams[OTHER[p.side]];
  const quote = (l: PropLine, side: 'over' | 'under'): BestLine => {
    const priced = !l.removed && pmfs[l.market] ? sideEv(pmfs[l.market]!, l, side) : undefined;
    return {
      book: l.book, side, line: l.line, odds: l[side]!, isRemoved: !!l.removed,
      ev: priced?.ev, pWin: priced === undefined ? undefined : priced.pWin * 100,
      betLimit: side === 'over' ? l.overLimit : l.underLimit, link: side === 'over' ? l.overLink : l.underLink,
    };
  };
  const fromPick = (pick: Pick): BestLine => quote(pick.propLine, pick.side);

  const best = bestLines(lines, pmfs, eligibility);
  const markets: Markets = {};
  for (const l of lines) {
    const market = l.market as MarketCode;
    const pmf = pmfs[l.market];
    // Admin seeds only: why subscribers don't see a projected market, as the server decided it.
    const why = pmf && p.hiddenBy?.[market];
    const m = (markets[market] ??= {
      projection: pmf ? meanFromPmf(pmf) : null, books: [], ...(pmf && { pmf, projectedMinutes: projection!.workload.minutes }), ...(why && { hiddenBy: why }),
      bestLines: { over: best[l.market]?.over && fromPick(best[l.market]!.over!), under: best[l.market]?.under && fromPick(best[l.market]!.under!) },
    });
    for (const side of ['over', 'under'] as const) if (l[side] !== undefined) m.books.push(quote(l, side) as BookLine);
  }
  const headline = bestMarket(best);
  const abbr = team ? team.abbr : '';

  return {
    id: p.playerId, name: p.name, team: abbr, position: p.position ?? '',
    imageUrl: getPlayerImageUrl(p.name, seed.sport, abbr || undefined, p.playerId) ?? undefined,
    game: { id: p.gameId, commenceTime: game?.commenceTime, opp: opponent ? opponent.abbr : '', isHome: p.side === 'home' },
    projectedMinutes: projection?.workload.minutes ?? null,
    teamNotes: team ? team.note : undefined,
    playerNotes: p.note,
    markets,
    bestMarket: headline?.ev === undefined ? undefined : { market: headline.propLine.market as MarketCode, ev: headline.ev, line: fromPick(headline) },
    updatedAt: new Date(Math.max(...lines.map(l => l.updatedAt))).toISOString(),
  };
}
